import React, { CSSProperties, useEffect, useMemo, useState } from 'react'
import { styled, Theme, CSSObject } from '@mui/material/styles'
import Box from '@mui/material/Box'
import MuiDrawer from '@mui/material/Drawer'
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import List from '@mui/material/List'
import CssBaseline from '@mui/material/CssBaseline'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Avatar from '@mui/material/Avatar'
import Tooltip from '@mui/material/Tooltip'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import {
  DashboardItem,
  ListItems,
  MainListItems,
  MaturityItem,
  SecondaryListItems,
} from './listItems'
import { useLocation, useNavigate } from 'react-router-dom'

import '../../i18n'
import { useTranslation } from 'react-i18next'

import LanguageSelector from '../../components/common/langSelector'
import UserMenu, { userMenuIcons } from '../../components/common/UserMenu'
import { useSelector } from 'react-redux'
import { getValidRoles } from '../../utils/checkRole'
import { canAccessLicenseModule } from '../../utils/licenseAccess'
import { selectUsername } from '../../redux/keycloak/keycloakSelectors'
import { useAppDispatch } from '../../redux/store'
import { logout } from '../../redux/keycloak/keycloakSlice'
import ExpiryBanner from '../../components/common/ExpiryBanner'
import { useLicense } from '../../hooks/useLicense'
import { getNavbarCrumbs } from './navCrumbs'
import {
  readSecurityNav,
  subscribeSecurityNav,
} from '../../utils/securityNav'
import {
  readEntityNav,
  subscribeEntityNav,
} from '../../utils/entityNav'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import { useOnboarding } from '../../features/onboarding/useOnboarding'

const SIDEBAR_BG =
  'linear-gradient(178deg, #050E2E 0%, #00146D 62%, #062A63 100%)'
const SIDEBAR_GLOW =
  'radial-gradient(460px 230px at 0% 0%, rgba(57, 202, 171, 0.20), transparent 72%)'
const SIDEBAR_LINE = 'rgba(255, 255, 255, 0.06)'
const TEAL_SOFT = '#7FE9D2'
const TOPBAR_H = 68
const DRAWER_W = 340
const DRAWER_W_COLLAPSED = 66
const SIDEBAR_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)'
const SIDEBAR_OPEN_MS = 460
const SIDEBAR_CLOSE_MS = 340

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

interface MainProps {
  children: React.ReactNode
  style?: CSSProperties
  showSidebar: boolean
}

const openedMixin = (): CSSObject => ({
  width: DRAWER_W,
  overflowX: 'hidden',
  transition: `width ${SIDEBAR_OPEN_MS}ms ${SIDEBAR_EASE}`,
})

const closedMixin = (): CSSObject => ({
  width: DRAWER_W_COLLAPSED,
  overflowX: 'hidden',
  transition: `width ${SIDEBAR_CLOSE_MS}ms ${SIDEBAR_EASE}`,
})

const DrawerHeader = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
}))

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'reserve',
})<AppBarProps & { reserve?: boolean }>(({ theme, open, reserve }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: `width ${SIDEBAR_CLOSE_MS}ms ${SIDEBAR_EASE}, margin ${SIDEBAR_CLOSE_MS}ms ${SIDEBAR_EASE}`,
  ...(open && {
    marginLeft: DRAWER_W,
    width: `calc(100% - ${DRAWER_W}px)`,
    transition: `width ${SIDEBAR_OPEN_MS}ms ${SIDEBAR_EASE}, margin ${SIDEBAR_OPEN_MS}ms ${SIDEBAR_EASE}`,
  }),
  ...(!open &&
    reserve && {
      marginLeft: DRAWER_W_COLLAPSED,
      width: `calc(100% - ${DRAWER_W_COLLAPSED}px)`,
    }),
}))

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ open }) => ({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open ? openedMixin() : closedMixin()),
  '& .MuiDrawer-paper': {
    ...(open ? openedMixin() : closedMixin()),
    backgroundColor: '#050E2E',
    backgroundImage: `${SIDEBAR_GLOW}, ${SIDEBAR_BG}`,
    borderRight: `1px solid ${SIDEBAR_LINE}`,
    boxShadow: 'none',
    color: '#C3CCE6',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
}))

export default function Main({
  children,
  style,
  showSidebar,
}: MainProps) {
  const { i18n, t } = useTranslation()
  const navigate = useNavigate()
  const { pathname, state: locationState } = useLocation()
  const selectedNorm =
    useSelector(selectSpecifications)[0]?.NormBase || ''
  const { restartTour } = useOnboarding()

  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    i18n.language
  )
  const [navTick, setNavTick] = useState(0)

  useEffect(() => {
    const bump = () => setNavTick((n) => n + 1)
    const unsubEntity = subscribeEntityNav(bump)
    const unsubSecurity = subscribeSecurityNav(bump)
    return () => {
      unsubEntity()
      unsubSecurity()
    }
  }, [])

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setSelectedLanguage(lng)
    }
    i18n.on('languageChanged', handleLanguageChange)
    return () => {
      i18n.off('languageChanged', handleLanguageChange)
    }
  }, [i18n])

  const handleLanguageSelect = (code: string) => {
    const next = code.slice(0, 2).toLowerCase()
    setSelectedLanguage(next)
    localStorage.setItem('i18nextLng', next)
    i18n.changeLanguage(next)
  }

  const path = pathname.replace(/\/+$/, '') || '/'
  const isViewAnalysisPage = path.includes('view-analysis')
  const isCreatePage = /(?:^|\/)add/i.test(path)
  const isGetDetailPage =
    /\/(?:mission-details|audit-detail|incident-detail|risque-detail|plan-detail|actiond|security-control-details|subDomain-Maturityscoring|subdomain|security-existing|security-subd|mission-priority|relatedDataMapped|related-controls|securityControlsToMap|norms)(?:\/|$)/i.test(
      path
    ) ||
    /\/security-controls\/[^/]+$/i.test(path) ||
    /\/security\/[^/]+$/i.test(path) ||
    /\/mission\/[^/]+\/projet\/[^/]+$/i.test(path) ||
    /\/mission\/[^/]+\/pipeline\/[^/]+$/i.test(path) ||
    /\/view-existing-control\//i.test(path)
  const collapseSidebar =
    isViewAnalysisPage || isCreatePage || isGetDetailPage

  const [pinnedOpen, setPinnedOpen] = React.useState(
    showSidebar && !collapseSidebar
  )
  const [hovering, setHovering] = React.useState(false)

  useEffect(() => {
    setHovering(false)
    if (!showSidebar || collapseSidebar) {
      setPinnedOpen(false)
      return
    }
    setPinnedOpen(true)
  }, [showSidebar, collapseSidebar])

  const open = pinnedOpen || (collapseSidebar && hovering)
  const shown = open

  const handleDrawerToggle = () => {
    setPinnedOpen((prev) => !prev)
    setHovering(false)
  }

  const roles = useSelector((state: any) => state.auth.roles)
  const validRoles = useMemo(() => getValidRoles(roles), [roles])
  const username = useSelector(selectUsername)
  const dispatch = useAppDispatch()

  const displayName = username || 'User'
  const userInitials = useMemo(() => {
    const name = displayName.trim()
    if (!name) return 'U'
    const parts = name.split(/[\s._-]+/).filter(Boolean)
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }, [displayName])

  const primaryRole = useMemo(() => {
    if (!validRoles || validRoles.length === 0) return 'User'
    const first = validRoles[0]
    if (first === 'RSSI') return i18n.language === 'fr' ? 'RSSI' : 'CISO'
    return first
  }, [validRoles, i18n.language])

  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const handleUserMenuOpen = () => setUserMenuOpen(true)
  const handleUserMenuClose = () => setUserMenuOpen(false)

  const isPrivileged = useMemo(
    () =>
      Array.isArray(roles) &&
      (roles.includes('RSSI') || roles.includes('ADMIN')),
    [roles]
  )
  const {
    plan,
    daysUntilExpiry,
    isExpiringSoon,
    initialized: licenseInitialized,
    isOwner,
  } = useLicense()

  const canAccessLicense = useMemo(
    () => canAccessLicenseModule(roles, isOwner),
    [roles, isOwner]
  )

  const navbarCrumbs = useMemo(() => {
    const security = readSecurityNav()
    const entity = readEntityNav()
    const state = (locationState || {}) as {
      familyId?: string
      familyLabel?: string
      controlLabel?: string
      missionId?: string
      missionLabel?: string
      pipelineId?: string
      pipelineLabel?: string
      projectId?: string
      projectLabel?: string
      taskId?: string
      taskLabel?: string
      auditId?: string
      auditLabel?: string
      incidentId?: string
      incidentLabel?: string
      riskId?: string
      riskLabel?: string
      planId?: string
      planLabel?: string
    }

    const missionFromPath =
      pathname.match(/^\/mission-details\/([^/]+)/)?.[1] ||
      pathname.match(/^\/mission\/view-analysis\/([^/]+)/)?.[1] ||
      pathname.match(/^\/mission\/([^/]+)(?:\/|$)/)?.[1]

    return getNavbarCrumbs(pathname, {
      norm: selectedNorm || security.norm,
      familyId: state.familyId || security.familyId,
      familyLabel: state.familyLabel || security.familyLabel,
      controlLabel: state.controlLabel || security.controlLabel,
      missionId: state.missionId || entity.missionId || missionFromPath,
      missionLabel: state.missionLabel || entity.missionLabel,
      pipelineId: state.pipelineId || entity.pipelineId,
      pipelineLabel: state.pipelineLabel || entity.pipelineLabel,
      projectId: state.projectId || entity.projectId,
      projectLabel: state.projectLabel || entity.projectLabel,
      taskId: state.taskId || entity.taskId,
      taskLabel: state.taskLabel || entity.taskLabel,
      auditId: state.auditId || entity.auditId,
      auditLabel: state.auditLabel || entity.auditLabel,
      incidentId: state.incidentId || entity.incidentId,
      incidentLabel: state.incidentLabel || entity.incidentLabel,
      riskId: state.riskId || entity.riskId,
      riskLabel: state.riskLabel || entity.riskLabel,
      planId: state.planId || entity.planId,
      planLabel: state.planLabel || entity.planLabel,
    })
    // navTick forces refresh when sessionStorage labels update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, locationState, selectedNorm, navTick])

  const planColors: Record<
    string,
    { bg: string; text: string; border: string }
  > = {
    basic: {
      bg: 'rgba(255,255,255,0.06)',
      text: '#C4D4D8',
      border: 'rgba(255,255,255,0.12)',
    },
    business: {
      bg: 'rgba(44,107,176,0.2)',
      text: '#9BC4F0',
      border: 'rgba(44,107,176,0.35)',
    },
    premium: {
      bg: 'rgba(13,155,135,0.18)',
      text: TEAL_SOFT,
      border: 'rgba(13,155,135,0.35)',
    },
  }
  const planStyle = plan ? planColors[plan] ?? planColors.basic : planColors.basic
  const days = daysUntilExpiry()

  const handleLogout = () => {
    handleUserMenuClose()
    dispatch(logout())
    window.location.reload()
  }

  const sectionTitleSx = {
    fontFamily: 'Lato, sans-serif',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.14em',
    color: '#54707F',
    textTransform: 'uppercase' as const,
    pl: '12px',
    pr: '12px',
    pt: 0,
    pb: '7px',
    display: 'block',
    whiteSpace: 'nowrap' as const,
  }

  const navGroupSx = {
    mb: '16px',
  }

  const sectionSlotSx = {
    height: 28,
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        minHeight: '100vh',
        bgcolor: showSidebar ? '#f1f4fa' : '#ffffff',
      }}
      data-tour-id="app-shell"
    >
      <CssBaseline />
      <AppBar
        position="fixed"
        open={open}
        reserve={showSidebar}
        sx={{
          height: TOPBAR_H,
          backgroundColor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(8px)',
          boxShadow: 'none',
          justifyContent: 'center',
          borderBottom: '1px solid #DFE7E8',
          ...( !showSidebar && {
            marginLeft: 0,
            width: '100%',
          }),
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, md: 3 },
            minHeight: `${TOPBAR_H}px !important`,
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              minWidth: 0,
              flex: 1,
            }}
          >
            {!showSidebar && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '9px',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: '#00146d',
                    boxShadow: 'inset 0 0 0 1px rgba(0,20,109,0.12)',
                  }}
                >
                  <img
                    src={require('./images/logo1-close.png')}
                    alt=""
                    style={{
                      height: 20,
                      width: 20,
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                </Box>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '15px',
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    color: '#00146d',
                    lineHeight: 1,
                  }}
                >
                  INCYIA
                </Typography>
                <Box
                  sx={{
                    width: '1px',
                    height: 22,
                    bgcolor: '#DFE7E8',
                    ml: 0.5,
                  }}
                />
              </Box>
            )}
            <Box
              component="nav"
              aria-label="Breadcrumb"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                minWidth: 0,
                height: 32,
                fontFamily: 'Lato, sans-serif',
                fontSize: '16px',
                lineHeight: '24px',
                color: '#5F7484',
              }}
            >
              {navbarCrumbs.map((crumb, index) => {
                const isLast = index === navbarCrumbs.length - 1
                const label =
                  crumb.label ||
                  t(crumb.labelKey || crumb.fallback, {
                    defaultValue: crumb.fallback,
                  })
                return (
                  <React.Fragment key={`${crumb.labelKey || crumb.fallback}-${index}`}>
                    {index > 0 && (
                      <Box
                        component="span"
                        aria-hidden
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          height: 24,
                          lineHeight: '24px',
                          color: '#8A9BA7',
                          fontWeight: 400,
                          fontSize: '16px',
                          flexShrink: 0,
                          transform: 'translateY(-0.5px)',
                        }}
                      >
                        ›
                      </Box>
                    )}
                    {isLast || !crumb.path ? (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          height: 24,
                          lineHeight: '24px',
                          fontSize: '16px',
                          fontWeight: isLast ? 600 : 500,
                          color: isLast ? '#0B1E2D' : '#5F7484',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {label}
                      </Box>
                    ) : (
                      <Box
                        component="button"
                        type="button"
                        onClick={() => navigate(crumb.path!)}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          height: 24,
                          lineHeight: '24px',
                          fontFamily: 'inherit',
                          fontSize: '16px',
                          fontWeight: 500,
                          color: '#5F7484',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          margin: 0,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          transition: 'color 0.14s',
                          '&:hover': { color: '#076B5C' },
                        }}
                      >
                        {label}
                      </Box>
                    )}
                  </React.Fragment>
                )
              })}
            </Box>
          </Box>

          <Box
            data-tour-id="topbar-workspace"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexShrink: 0,
              ml: 'auto',
            }}
          >
            <Box
              data-tour-id="topbar-language-selector"
              sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
            >
              <LanguageSelector
                selectedLanguage={selectedLanguage}
                onSelect={handleLanguageSelect}
              />
            </Box>

            <Box
              sx={{
                width: '1px',
                height: 28,
                bgcolor: '#DFE7E8',
                flexShrink: 0,
              }}
            />

            <ClickAwayListener onClickAway={handleUserMenuClose}>
              <div>
                <UserMenu
                  open={userMenuOpen}
                  onOpen={handleUserMenuOpen}
                  onClose={handleUserMenuClose}
                  displayName={displayName}
                  primaryRole={primaryRole}
                  initials={userInitials}
                  onLogout={handleLogout}
                  items={[
                    {
                      key: 'profile',
                      label: t('profile'),
                      icon: userMenuIcons.profile,
                      onClick: () => navigate('/profile'),
                    },
                    {
                      key: 'settings',
                      label: t('settings'),
                      icon: userMenuIcons.settings,
                      onClick: () => navigate('/settings'),
                      show: isPrivileged,
                    },
                    {
                      key: 'team',
                      label: t('team'),
                      icon: userMenuIcons.team,
                      onClick: () => navigate('/team'),
                    },
                    {
                      key: 'license',
                      label: t('licensePage.navLabel'),
                      icon: userMenuIcons.license,
                      onClick: () => navigate('/license'),
                      show: canAccessLicense,
                    },
                    {
                      key: 'tour',
                      label: t('onboarding.restart'),
                      icon: userMenuIcons.tour,
                      onClick: restartTour,
                    },
                  ]}
                />
              </div>
            </ClickAwayListener>
          </Box>
        </Toolbar>
      </AppBar>

      {showSidebar && (
        <Drawer
          variant="permanent"
          open={open}
          onMouseEnter={() => {
            if (collapseSidebar) setHovering(true)
          }}
          onMouseLeave={() => setHovering(false)}
        >
          <DrawerHeader
            sx={{
              background: 'transparent',
              borderBottom: `1px solid ${SIDEBAR_LINE}`,
              flexDirection: shown ? 'row' : 'column',
              justifyContent: 'center',
              alignItems: 'center',
              px: shown ? '14px' : 0.75,
              py: 0,
              height: 88,
              overflow: 'hidden',
              gap: shown ? 1.25 : 0.75,
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Box
              onClick={() => navigate('/')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.1,
                minWidth: 0,
                overflow: 'hidden',
                cursor: 'pointer',
                flex: shown ? 1 : 'none',
                justifyContent: shown ? 'flex-start' : 'center',
                width: shown ? 'auto' : '100%',
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '8px',
                  overflow: 'hidden',
                  flex: 'none',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'rgba(0,0,0,0.25)',
                  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.12)',
                  mx: shown ? 0 : 'auto',
                }}
              >
                <img
                  src={require('./images/logo1-close.png')}
                  alt=""
                  style={{
                    height: '22px',
                    width: '22px',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </Box>
              {shown && (
                <Typography
                  component="span"
                  sx={{
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '1rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    color: '#ffffff',
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                  }}
                >
                  INCYIA
                </Typography>
              )}
            </Box>

            <IconButton
              aria-label={shown ? 'collapse sidebar' : 'expand sidebar'}
              onClick={handleDrawerToggle}
              disableRipple
              sx={{
                width: 26,
                height: 26,
                borderRadius: '6px',
                color: '#8DA3AD',
                flex: 'none',
                p: 0,
                ml: shown ? 'auto' : 0,
                bgcolor: shown ? 'transparent' : 'rgba(255,255,255,0.06)',
                transition: 'background 0.15s, color 0.15s',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.08)',
                  color: '#fff',
                },
                '& svg': {
                  fontSize: 18,
                  transition: `transform ${SIDEBAR_OPEN_MS}ms ${SIDEBAR_EASE}`,
                  transform: shown ? 'none' : 'rotate(180deg)',
                },
              }}
            >
              <ChevronLeftRoundedIcon />
            </IconButton>
          </DrawerHeader>

          <List
            component="nav"
            disablePadding
            sx={{
              pt: '16px',
              pb: '12px',
              px: '12px',
              overflowX: 'hidden',
              overflowY: 'auto',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              zIndex: 1,
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                borderRadius: 99,
              },
            }}
            data-tour-id="sidebar-navigation"
          >
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={navGroupSx}>
                <DashboardItem open={shown} />
              </Box>

              <Box sx={navGroupSx} data-tour-id="nav-group-governance">
                <Box sx={sectionSlotSx}>
                  {shown ? (
                    <Typography sx={sectionTitleSx}>
                      {t('governance')}
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        borderTop: `1px solid ${SIDEBAR_LINE}`,
                        width: '100%',
                        mx: 0.5,
                      }}
                    />
                  )}
                </Box>
                <MaturityItem open={shown} />
              </Box>

              <Box sx={navGroupSx} data-tour-id="nav-group-management">
                <Box sx={sectionSlotSx}>
                  {shown ? (
                    <Typography sx={sectionTitleSx}>{t('managment')}</Typography>
                  ) : (
                    <Box
                      sx={{
                        borderTop: `1px solid ${SIDEBAR_LINE}`,
                        width: '100%',
                        mx: 0.5,
                      }}
                    />
                  )}
                </Box>
                <MainListItems open={shown} />
              </Box>

              <Box sx={navGroupSx} data-tour-id="nav-group-sources">
                <Box sx={sectionSlotSx}>
                  {shown ? (
                    <Typography sx={sectionTitleSx}>{t('sources')}</Typography>
                  ) : (
                    <Box
                      sx={{
                        borderTop: `1px solid ${SIDEBAR_LINE}`,
                        width: '100%',
                        mx: 0.5,
                      }}
                    />
                  )}
                </Box>
                <SecondaryListItems open={shown} />
              </Box>

              <Box sx={{ mb: 0 }}>
                <Box sx={sectionSlotSx}>
                  {shown ? (
                    <Typography sx={sectionTitleSx}>
                      {t('securityControls')}
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        borderTop: `1px solid ${SIDEBAR_LINE}`,
                        width: '100%',
                        mx: 0.5,
                      }}
                    />
                  )}
                </Box>
                <ListItems open={shown} />
              </Box>
            </Box>

            {shown && licenseInitialized && plan && canAccessLicense && (
              <Box
                onClick={
                  canAccessLicense ? () => navigate('/license') : undefined
                }
                sx={{
                  mx: 1.25,
                  mb: 0.5,
                  px: 1.5,
                  py: 1,
                  borderRadius: '8px',
                  border: `1px solid ${planStyle.border}`,
                  bgcolor: planStyle.bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  cursor: canAccessLicense ? 'pointer' : 'default',
                  transition: 'filter 0.15s ease',
                  '&:hover': canAccessLicense
                    ? { filter: 'brightness(1.08)' }
                    : undefined,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      color: planStyle.text,
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      lineHeight: 1,
                    }}
                  >
                    {t(
                      `license.packs.${
                        plan === 'premium'
                          ? 'premium'
                          : plan === 'business'
                            ? 'business'
                            : 'standard'
                      }`,
                      plan
                    )}
                  </Typography>
                  {days !== null && days > 0 && (
                    <Typography
                      sx={{
                        fontFamily: 'Lato, sans-serif',
                        fontSize: '0.65rem',
                        color: isExpiringSoon() ? '#F0A070' : '#6B8593',
                        fontWeight: isExpiringSoon() ? 600 : 400,
                        mt: 0.35,
                        lineHeight: 1,
                      }}
                    >
                      {t('license.badge.daysLeft', { count: days })}
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: isExpiringSoon() ? '#F0A070' : '#12C4A9',
                    flexShrink: 0,
                  }}
                />
              </Box>
            )}

            {shown ? (
              <Box
                sx={{
                  mt: 1,
                  mx: 0,
                  px: 1.5,
                  py: 1.5,
                  borderTop: `1px solid ${SIDEBAR_LINE}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                }}
              >
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    bgcolor: 'rgba(255,255,255,0.1)',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: TEAL_SOFT,
                    border: '1px solid rgba(255,255,255,0.14)',
                    flexShrink: 0,
                  }}
                >
                  {userInitials}
                </Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#E4EEF0',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: 1.25,
                      textTransform: 'capitalize',
                    }}
                  >
                    {displayName}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '12px',
                      color: '#6B8593',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      lineHeight: 1.3,
                      mt: 0.25,
                      textTransform: 'uppercase',
                    }}
                  >
                    {primaryRole}
                  </Typography>
                </Box>
                <Tooltip
                  title={t('logout.button') as string}
                  placement="top"
                  arrow
                >
                  <IconButton
                    onClick={handleLogout}
                    size="small"
                    disableRipple
                    sx={{
                      width: 26,
                      height: 26,
                      color: '#6B8593',
                      borderRadius: '6px',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.08)',
                        color: '#fff',
                      },
                    }}
                  >
                    <LogoutRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Tooltip
                title={`${displayName} · ${primaryRole}`}
                placement="right"
                arrow
              >
                <Box
                  sx={{
                    mt: 1,
                    mx: 'auto',
                    py: 1.5,
                    borderTop: `1px solid ${SIDEBAR_LINE}`,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Avatar
                    sx={{
                      width: 34,
                      height: 34,
                      bgcolor: 'rgba(255,255,255,0.1)',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: TEAL_SOFT,
                      border: '1px solid rgba(255,255,255,0.14)',
                      cursor: 'pointer',
                    }}
                  >
                    {userInitials}
                  </Avatar>
                </Box>
              </Tooltip>
            )}
          </List>
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flex: '1 1 0',
          p: showSidebar ? { xs: 2, md: '26px 32px 72px' } : 0,
          minWidth: 0,
          width: 'auto',
          minHeight: '100vh',
          overflow: 'visible',
          // Match sign-in canvas (#f1f4fa) across the app shell
          bgcolor: showSidebar ? '#f1f4fa' : '#ffffff',
        }}
      >
        <ExpiryBanner />
        <div
          className="w-full max-w-full"
          style={{
            marginTop: `${TOPBAR_H + 8}px`,
            ...style,
          }}
        >
          {children}
        </div>
      </Box>
    </Box>
  )
}
